import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useInstatDesign } from '../theme/instatDesign';

/**
 * Minimal Apple-style sparkline — smoothed line + soft gradient fill below.
 * Auto-scaled, draws dots at min & max.
 *
 * Props:
 *   data       : number[] (or array of { value, label? })
 *   width/height : viewport (fluid-scales anyway)
 *   color      : stroke color (defaults to accent)
 *   showDots   : draw endpoints + min/max
 *   strokeWidth
 */
export default function Sparkline({
  data = [],
  width = 160,
  height = 44,
  color,
  showDots = true,
  strokeWidth = 2,
  fill = true,
  sx = {},
}) {
  const d = useInstatDesign();
  const stroke = color || d.accent;

  const { pts, minIdx, maxIdx, path, area } = useMemo(() => {
    const values = (data || [])
      .map((v) => (typeof v === 'object' ? v?.value : v))
      .map((v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v)));

    const valid = values.filter((v) => v != null);
    if (valid.length < 2) {
      return { pts: [], minIdx: -1, maxIdx: -1, path: '', area: '' };
    }

    const minV = Math.min(...valid);
    const maxV = Math.max(...valid);
    const range = maxV - minV || 1;
    const padX = 4;
    const padY = 6;
    const w = width - padX * 2;
    const h = height - padY * 2;
    const stepX = w / (values.length - 1);

    const rawPts = values.map((v, i) => {
      if (v == null) return null;
      const x = padX + i * stepX;
      const y = padY + h - ((v - minV) / range) * h;
      return { x, y, v, i };
    });
    const pts = rawPts.filter(Boolean);

    // Catmull–Rom → smooth path (small tension, Apple-ish)
    const toPath = (points) => {
      if (!points.length) return '';
      const cr = 0.18; // curvature
      let out = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i === 0 ? 0 : i - 1];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
        const cp1x = p1.x + (p2.x - p0.x) * cr;
        const cp1y = p1.y + (p2.y - p0.y) * cr;
        const cp2x = p2.x - (p3.x - p1.x) * cr;
        const cp2y = p2.y - (p3.y - p1.y) * cr;
        out += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
      }
      return out;
    };

    const path = toPath(pts);
    const area = path
      ? `${path} L ${pts[pts.length - 1].x.toFixed(2)} ${height - padY} L ${pts[0].x.toFixed(2)} ${height - padY} Z`
      : '';

    const minIdx = pts.reduce((acc, p) => (p.v < pts[acc].v ? p.i : acc), 0);
    const maxIdx = pts.reduce((acc, p) => (p.v > pts[acc].v ? p.i : acc), 0);

    return { pts, minIdx, maxIdx, path, area };
  }, [data, width, height]);

  if (!pts.length) {
    return (
      <Box sx={{ width, height, ...sx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: '70%', height: 1, bgcolor: d.colors.dividerStrong }} />
      </Box>
    );
  }

  const gradId = `spark-grad-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <Box sx={{ width, height, ...sx }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="100%"
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={stroke} stopOpacity="0.28" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        {fill && area && <path d={area} fill={`url(#${gradId})`} />}
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {showDots && pts.length > 0 && (
          <>
            {/* Min */}
            <Tooltip title={`Low: ${pts.find((p) => p.i === minIdx)?.v ?? ''}`} arrow>
              <circle
                cx={pts.find((p) => p.i === minIdx)?.x}
                cy={pts.find((p) => p.i === minIdx)?.y}
                r="2.5"
                fill={d.colors.surface}
                stroke={d.colors.textTertiary}
                strokeWidth="1.5"
              />
            </Tooltip>
            {/* Max */}
            <Tooltip title={`High: ${pts.find((p) => p.i === maxIdx)?.v ?? ''}`} arrow>
              <circle
                cx={pts.find((p) => p.i === maxIdx)?.x}
                cy={pts.find((p) => p.i === maxIdx)?.y}
                r="2.8"
                fill={stroke}
              />
            </Tooltip>
            {/* Last */}
            <circle
              cx={pts[pts.length - 1].x}
              cy={pts[pts.length - 1].y}
              r="3.2"
              fill={d.colors.surface}
              stroke={stroke}
              strokeWidth="1.8"
            />
          </>
        )}
      </svg>
    </Box>
  );
}
